import { CookieConsentBar } from 'components/layout/CookieConsentBar/CookieConsentBar.js';
import { GlobalErrorHandler } from 'components/layout/GlobalErrorHandler/index.js';
import { Backdrop } from 'components/ui/Backdrop/Backdrop.js';
import { ConfirmModal } from 'components/ui/modals/ConfirmModal/index.js';
import {
  LoginPage,
  LoginSuccessPage,
} from 'features/onboarding-auth/index.js';
import {
  getPermissions,
  PermissionContext,
} from 'features/units/index.js';
import type {
  FC,
  PropsWithChildren,
} from 'react';
import { useMemo } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useMatch,
  useNavigate,
} from 'react-router';
import {
  AuthContext,
  useAuthContext,
} from 'shared/context/auth/useAuth.js';
import {
  GlobalErrorContext,
  useGlobalErrorContextProvider,
} from 'shared/context/globalError.js';
import {
  AbilityContext,
  createUserAbilities,
} from 'shared/context/permissions/usePermissions.js';
import { useCompanyContext } from 'shared/context/useCompanyContext.js';
import type { CompanyContextProps } from 'shared/context/useCurrentCompany.js';
import { CompanyContext } from 'shared/context/useCurrentCompany.js';
import { useMyUser } from 'shared/context/useMyUser.js';
import { useAppAuth } from 'shared/hooks/auth/useAppAuth.js';
import {
  DEFAULT_LOGGED_PAGE,
  DEFAULT_NOT_LOGGED_PAGE,
  RoutePaths,
} from 'shared/routes.js';
import { ErrorStatus } from 'shared/utils/enums.js';

import { useInitializeApiConfig } from './api.config.js';
import {
  ClearCacheContext,
  useAutoClearCache,
  useClearCacheContextProvider,
} from './hooks/useClearCache.js';
import { usePublicRoutes } from './hooks/usePublicRoutes.js';
import { useRegistryAdminRoutes } from './hooks/useRegistryAdminRoutes.js';
import { useRegularRoutes } from './hooks/useRegularRoutes.js';
import { useTokenRefresher } from './hooks/useTokenRefresher.js';
import { RegisterRouter } from './routers/RegisterRouter.js';
import { isResponseError } from './utils/response.js';
import { QueryClientProvider } from './utils/useQueryClient.js';

const ContextfulApp = () => {
  const { isAuthenticated, logout } = useAppAuth();
  const { user, isLoading: isLoadingUser, error } = useMyUser();
  const navigate = useNavigate();
  const registryAdminRoutes = useRegistryAdminRoutes();
  const regularRoutes = useRegularRoutes();

  if (isLoadingUser) {
    return <Backdrop open={true} />;
  }

  if (isAuthenticated && isResponseError(error, ErrorStatus.Unauthorized)) {
    return (
      <ConfirmModal
        open
        actions={[
          {
            title: 'Go back to home page',
            onClick: () => {
              void logout();
              void navigate(RoutePaths.HOME);
            },
            buttonProps: { variant: 'contained' },
          },
        ]}
      >
        Sorry, we couldn&apos;t find your profile.
      </ConfirmModal>
    );
  }

  return (
    <Routes>
      {user?.isRegistryAdmin ? registryAdminRoutes : regularRoutes}
      <Route
        path="*"
        element={(
          <Navigate
            to={
              Boolean(isAuthenticated)
                ? DEFAULT_LOGGED_PAGE
                : DEFAULT_NOT_LOGGED_PAGE
            }
          />
        )}
      />
    </Routes>
  );
};

const PrivateApp: FC<PropsWithChildren> = () => {
  const { companyId, companyName } = useCompanyContext();
  const { user } = useMyUser();
  const userAbilities = useMemo(
    () => createUserAbilities({ user, companyId }),
    [user, companyId],
  );
  /** New concept to replace abilities */
  const userPermissions = useMemo(
    () => getPermissions({ user }),
    [user],
  );

  return (
    <CompanyContext.Provider
      key={companyId}
      value={{ companyId, companyName } as CompanyContextProps}
    >
      <AbilityContext.Provider value={userAbilities}>
        <PermissionContext.Provider value={userPermissions}>
          <CookieConsentBar />
          <ContextfulApp />
        </PermissionContext.Provider>
      </AbilityContext.Provider>
    </CompanyContext.Provider>
  );
};

const AuthorizedApp: FC<PropsWithChildren> = () => {
  const { isAuthenticated } = useAppAuth();

  if (!isAuthenticated) {
    return <Navigate to={DEFAULT_NOT_LOGGED_PAGE} />;
  }

  return (
    <Routes>
      <Route path={RoutePaths.APP_INIT} element={<LoginSuccessPage />} />
      <Route path="*" element={<PrivateApp />} />
    </Routes>
  );
};

const PublicApp = () => {
  const publicRoutes = usePublicRoutes();
  useInitializeApiConfig();
  useAutoClearCache();
  useTokenRefresher();

  return (
    <Routes>
      {publicRoutes}
      {RegisterRouter}
      <Route path={RoutePaths.LOGIN} element={<LoginPage />} />
      <Route path="*" element={<AuthorizedApp />} />
    </Routes>
  );
};

export const App = () => {
  const clearCacheValue = useClearCacheContextProvider();
  const tokenValue = useAuthContext();
  const errorContextValue = useGlobalErrorContextProvider();

  return (
    <GlobalErrorContext.Provider value={errorContextValue}>
      <AuthContext.Provider value={tokenValue}>
        <ClearCacheContext.Provider value={clearCacheValue}>
          <QueryClientProvider>
            <CookieConsentBar />
            <GlobalErrorHandler />
            <PublicApp />
          </QueryClientProvider>
        </ClearCacheContext.Provider>
      </AuthContext.Provider>
    </GlobalErrorContext.Provider>
  );
};
